/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { databaseService } from '@/services/database/database.service.js'
import { goatRepository } from '@/services/repositories/goat.repository.js'
import { weightRepository } from '@/services/repositories/weight.repository.js'
import { temperatureRepository } from '@/services/repositories/temperature.repository.js'
import { famachaRepository } from '@/services/repositories/famacha.repository.js'

/**
 * User Testing Data Seeder
 * Creates fixed test data for user testing purposes
 * This ensures all testers work with identical data
 */

/**
 * Clear the entire database
 */
async function clearDatabase() {
  console.log('Clearing database for test data...')

  try {
    // Delete child tables first (to avoid foreign key constraints)
    await databaseService.run('DELETE FROM temperatures', [])
    console.log('Cleared temperatures table')
    
    await databaseService.run('DELETE FROM famacha', [])
    console.log('Cleared famacha table')
    
    await databaseService.run('DELETE FROM dag', [])
    console.log('Cleared dag table')
    
    await databaseService.run('DELETE FROM weights', [])
    console.log('Cleared weights table')
    
    await databaseService.run('DELETE FROM goats', [])
    console.log('Cleared goats table')
    
    await databaseService.run('DELETE FROM groups', [])
    console.log('Cleared groups table')

    console.log('Database cleared')
  } catch (error) {
    console.error('Failed to clear database:', error)
    throw error
  }
}

/**
 * Parse German date format (DD.MM.YY) to ISO format (YYYY-MM-DD)
 */
function parseGermanDate(dateStr) {
  const [day, month, year] = dateStr.split('.')
  const fullYear = year.length === 2 ? `20${year}` : year
  return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

/**
 * Parse German date format (DD.MM.YY) to ISO timestamp
 */
function parseGermanDateTime(dateStr) {
  const isoDate = parseGermanDate(dateStr)
  return new Date(`${isoDate}T12:00:00Z`).toISOString()
}

/**
 * Seed the two test goats with fixed data
 */
export async function seedTestData() {
  console.log('Starting test data seeding...')
  
  try {
    // Step 1: Clear database
    await clearDatabase()
    
    // Step 2: Create Goat 1 (Lucy)
    console.log('\nCreating Goat 1: Lucy...')
    const lucy = {
      eartag: 'DE 0106 123 25112', // Using new format with prefix
      name: 'Lucy',
      dateOfBirth: parseGermanDate('15.08.21'),
      placeOfBirth: 'Gießen',
      breed: 'Burenziege',
      gender: 'Female',
      groupId: null
    }
    const lucyId = await goatRepository.create(lucy)
    console.log(`  Created Lucy (ID: ${lucyId})`)
    
    // Step 3: Seed weights for Lucy
    console.log('\nSeeding weights for Lucy...')
    await weightRepository.create({
      goatId: lucyId,
      value: 66.0,
      measuredAt: parseGermanDateTime('14.09.24')
    })
    await weightRepository.create({
      goatId: lucyId,
      value: 67.5,
      measuredAt: parseGermanDateTime('04.02.25')
    })
    await weightRepository.create({
      goatId: lucyId,
      value: 70.0,
      measuredAt: parseGermanDateTime('24.11.25')
    })
    console.log('Created 3 weight records for Lucy')
    
    // Step 4: Seed temperatures for Lucy
    console.log('\nSeeding temperatures for Lucy...')
    await temperatureRepository.create({
      goatId: lucyId,
      value: 38.0,
      measuredAt: parseGermanDateTime('14.09.24')
    })
    await temperatureRepository.create({
      goatId: lucyId,
      value: 38.1,
      measuredAt: parseGermanDateTime('04.02.25')
    })
    await temperatureRepository.create({
      goatId: lucyId,
      value: 37.8,
      measuredAt: parseGermanDateTime('24.11.25')
    })
    console.log('Created 3 temperature records for Lucy')
    
    // Step 5: Seed FAMACHA scores for Lucy
    console.log('\nSeeding FAMACHA scores for Lucy...')
    await famachaRepository.create({
      goatId: lucyId,
      score: 2,
      measuredAt: parseGermanDateTime('14.09.24')
    })
    await famachaRepository.create({
      goatId: lucyId,
      score: 1,
      measuredAt: parseGermanDateTime('04.02.25')
    })
    await famachaRepository.create({
      goatId: lucyId,
      score: 1,
      measuredAt: parseGermanDateTime('24.11.25')
    })
    console.log('Created 3 FAMACHA records for Lucy')
    
    console.log('\nTest data seeding complete!')
    console.log('Summary: 1 goat (Lucy) with 3 weight, temperature, and FAMACHA records')
    
  } catch (error) {
    console.error('Failed to seed test data:', error)
    throw error
  }
}
